import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { subscribe } from 'lib/pusher';
import type { InterestWorkspaceResponse } from 'types/api-responses';

import { promotionPendingAtom } from './atoms';

/** Pusher event broadcast by Promoter on the entity channel. */
const CERTIFIED_EVENT = 'interest-certified';

/**
 * Raw (snake_case) Pusher payload — Pusher messages are NOT run through the
 * camelizing REST serializer, so keys match the Ruby PushNotification payload.
 */
interface CertifiedPusherPayload {
  entity_id: number;
  promoted_workspace_id: number;
  promoted_at: string;
  promoted_by_id: number | null;
  promoted_by_name: string | null;
}

/**
 * Subscribes the open workspace to its entity-scoped Pusher channel and flags a
 * pending promotion when ANOTHER workspace for the same entity is promoted
 * (INS-9740). Self-promotions (this workspace) are ignored. Setting the flag
 * only surfaces the non-intrusive banner — it never refetches or interrupts the
 * user; the pull-in is opt-in.
 *
 * @param {InterestWorkspaceResponse} workspace - The loaded workspace
 *   (provides `pusherChannel` + `id`).
 * @return {void}
 */
export const useEntityPromotionChannel = (
  workspace: InterestWorkspaceResponse
): void => {
  const setPending = useSetAtom(promotionPendingAtom);

  useEffect(() => {
    const channel = workspace.pusherChannel;
    if (!channel) return () => undefined;

    const subscription = subscribe(channel);

    const handleCertified = (data: CertifiedPusherPayload) => {
      if (data.promoted_workspace_id === workspace.id) return; // self

      setPending({
        entityId: data.entity_id,
        promotedWorkspaceId: data.promoted_workspace_id,
        promotedAt: data.promoted_at,
        promotedById: data.promoted_by_id,
        promotedByName: data.promoted_by_name,
      });
    };

    subscription.bind(CERTIFIED_EVENT, handleCertified);

    return () => {
      subscription.unbind(CERTIFIED_EVENT, handleCertified);
      subscription.unsubscribe();
    };
  }, [workspace.pusherChannel, workspace.id, setPending]);
};
